import { ServiceHistoryStatus, VehicleBuyInSignatureDevice, VehicleBuyInType, VehicleUsage } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import type { VehicleBuyInByUuidResponse } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-by-uuid.query";
import { getCustomerTypeLabel } from "@/common/helpers/customer-display";
import { apiYearMonthToInputString } from "@/common/helpers/date-input";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { CommandButton } from "@/components/command-palette/command-button";
import { ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { VehicleBuyInStatusBadge } from "@/components/vehicle-buy-in/vehicle-buy-in-status-badge";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  shouldShowVehicleAccidentDetails,
} from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import {
  getVehicleBuyInColorLabel,
  getVehicleBuyInCustomerFieldVisibility,
} from "@/modules/fab/vehicle-buy-in/_helpers/vehicle-buy-in-detail.helpers";
import { DetailInfoRow } from "@/views/vehicle-valuation-detail/_components/detail-info-row";
import { EyeIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VehicleBuyInSnapshotDetailsProps {
  vehicleBuyIn: VehicleBuyInByUuidResponse;
}

const SERVICE_HISTORY_LABEL_KEYS = {
  [ServiceHistoryStatus.UNKNOWN]: "masterdata:ServiceHistoryUnknown",
  [ServiceHistoryStatus.NONE]: "masterdata:ServiceHistoryNone",
  [ServiceHistoryStatus.PARTIAL]: "masterdata:ServiceHistoryPartial",
  [ServiceHistoryStatus.FullServiceHistory]: "masterdata:ServiceHistoryFull",
  [ServiceHistoryStatus.FullDealerServiceHistory]: "masterdata:ServiceHistoryFullDealer",
} as const satisfies Record<ServiceHistoryStatus, string>;

const VEHICLE_USAGE_LABEL_KEYS = {
  [VehicleUsage.RegularPersonalCommercial]: "masterdata:VehicleUsageRegular",
  [VehicleUsage.RENTAL]: "masterdata:VehicleUsageRental",
  [VehicleUsage.TaxiRideHailing]: "masterdata:VehicleUsageTaxi",
  [VehicleUsage.DrivingSchool]: "masterdata:VehicleUsageDrivingSchool",
  [VehicleUsage.DEMO]: "masterdata:VehicleUsageDemo",
  [VehicleUsage.FleetPool]: "masterdata:VehicleUsageFleetPool",
  [VehicleUsage.DeliveryCourier]: "masterdata:VehicleUsageDelivery",
  [VehicleUsage.SpecialPurpose]: "masterdata:VehicleUsageSpecialPurpose",
  [VehicleUsage.CONSTRUCTION]: "masterdata:VehicleUsageConstruction",
  [VehicleUsage.ManagementCompanyCar]: "masterdata:VehicleUsageManagementCompanyCar",
  [VehicleUsage.EmployeeCompanyCar]: "masterdata:VehicleUsageEmployeeCompanyCar",
  [VehicleUsage.UNKNOWN]: "masterdata:VehicleUsageUnknown",
} as const satisfies Record<VehicleUsage, string>;

const VEHICLE_BUY_IN_TYPE_LABEL_KEYS = {
  [VehicleBuyInType.TradeIn]: "fab:VehicleBuyInTypeTradeIn",
  [VehicleBuyInType.Purchase]: "fab:VehicleBuyInTypePurchase",
} as const satisfies Record<VehicleBuyInType, string>;

const SIGNATURE_DEVICE_LABEL_KEYS = {
  [VehicleBuyInSignatureDevice.Company]: "fab:UseBranchDevice",
  [VehicleBuyInSignatureDevice.Customer]: "fab:UseCustomerEmailAddress",
} as const satisfies Record<VehicleBuyInSignatureDevice, string>;

const serviceHistoryValues = new Set<string>(Object.values(ServiceHistoryStatus));
const vehicleUsageValues = new Set<string>(Object.values(VehicleUsage));

function normalizeText(value: string | null | undefined): string | undefined {
  return value?.trim() || undefined;
}

function renderText(value: string | null | undefined) {
  return automaticCellRenderer(normalizeText(value), { convertString: false });
}

function renderDate(value: string | null | undefined) {
  return automaticCellRenderer(normalizeText(value), { dateFormat: "date" });
}

function renderCurrency(value: number) {
  return automaticCellRenderer(value, { numberFormat: "currency", numberDecimals: 2 });
}

function renderInteger(value: number, suffix = "") {
  return automaticCellRenderer(value, { numberDecimals: 0, globalSuffix: suffix });
}

function isServiceHistoryStatus(value: string): value is ServiceHistoryStatus {
  return serviceHistoryValues.has(value);
}

function isVehicleUsage(value: string): value is VehicleUsage {
  return vehicleUsageValues.has(value);
}

export function VehicleBuyInSnapshotDetails({ vehicleBuyIn }: VehicleBuyInSnapshotDetailsProps) {
  const { t, i18n } = useTranslation(["common", "masterdata", "fab"]);
  const { branch, customer, employee, purchase, signatureDevice, vehicle } = vehicleBuyIn.documentSnapshot;
  const { showBirthDate, showBusinessFields } = getVehicleBuyInCustomerFieldVisibility(customer.type);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("fab:VehicleBuyInProcess")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailInfoRow label={t("common:Status")} value={<VehicleBuyInStatusBadge status={vehicleBuyIn.status} />} />
          <DetailInfoRow
            label={t("common:CreatedAt")}
            value={automaticCellRenderer(vehicleBuyIn.createdAt, { dateFormat: "datetime" })}
          />
          <DetailInfoRow
            label={t("fab:VehicleBuyInV1TransferAt")}
            value={automaticCellRenderer(vehicleBuyIn.v1TransferAt, { dateFormat: "datetime" })}
          />
          <DetailInfoRow
            label={t("common:UUID")}
            value={renderText(vehicleBuyIn.documentSnapshot.vehicleBuyInUuid)}
            mono
          />
          <DetailInfoRow
            label={t("fab:SignatureDevice")}
            value={signatureDevice ? t(SIGNATURE_DEVICE_LABEL_KEYS[signatureDevice]) : renderText(signatureDevice)}
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("fab:Branch")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <DetailInfoRow label={t("common:Name")} value={renderText(branch.name)} />
            <DetailInfoRow label={t("common:Company")} value={renderText(branch.companyName)} />
            <DetailInfoRow label={t("common:Street")} value={renderText(branch.address.street)} />
            <DetailInfoRow label={t("masterdata:HouseNumber")} value={renderText(branch.address.houseNumber)} />
            <DetailInfoRow label={t("masterdata:PostalCode")} value={renderText(branch.address.postalCode)} />
            <DetailInfoRow label={t("masterdata:City")} value={renderText(branch.address.city)} />
            <DetailInfoRow label={t("masterdata:Country")} value={renderText(branch.address.country)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("fab:Employee")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <DetailInfoRow label={t("common:FirstName")} value={renderText(employee.firstName)} />
            <DetailInfoRow label={t("common:LastName")} value={renderText(employee.lastName)} />
            <DetailInfoRow label={t("common:Email")} value={renderText(employee.email)} fullWidth />
            <DetailInfoRow label={t("common:Occupation")} value={renderText(employee.occupation)} fullWidth />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("fab:Customer")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailInfoRow label={t("fab:CustomerType")} value={getCustomerTypeLabel(customer.type)} />
          {showBusinessFields ? (
            <>
              <DetailInfoRow label={t("fab:CompanyName")} value={renderText(customer.companyName)} />
              <DetailInfoRow label={t("fab:RegistrationNumber")} value={renderText(customer.registrationNumber)} />
            </>
          ) : null}
          <DetailInfoRow label={t("common:FirstName")} value={renderText(customer.firstName)} />
          <DetailInfoRow label={t("common:LastName")} value={renderText(customer.lastName)} />
          {showBirthDate ? <DetailInfoRow label={t("fab:BirthDate")} value={renderDate(customer.birthDate)} /> : null}
          <DetailInfoRow label={t("fab:CustomerNumber")} value={renderText(customer.customerNumber)} />
          <DetailInfoRow label={t("common:Email")} value={renderText(customer.email)} />
          <DetailInfoRow label={t("common:Phone")} value={renderText(customer.phoneNumber)} />
          <DetailInfoRow label={t("common:Street")} value={renderText(customer.address.street)} />
          <DetailInfoRow label={t("masterdata:HouseNumber")} value={renderText(customer.address.houseNumber)} />
          <DetailInfoRow label={t("masterdata:PostalCode")} value={renderText(customer.address.postalCode)} />
          <DetailInfoRow label={t("masterdata:City")} value={renderText(customer.address.city)} />
          <DetailInfoRow label={t("masterdata:Country")} value={renderText(customer.address.country)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>{t("fab:VehicleInformation")}</CardTitle>
            {vehicleBuyIn.vehicle?.uuid ? (
              <Can I="read" a="Vehicle">
                <CommandButton
                  commandId={`vehicle-details-${vehicleBuyIn.vehicle.uuid}`}
                  label={t("masterdata:VehicleDetails")}
                  icon={<EyeIcon />}
                  to={`~/masterdata/vehicle/${vehicleBuyIn.vehicle.uuid}`}
                  size={ButtonSize.Sm}
                  variant={ButtonVariant.Outlined}
                  colorVariant={ButtonColorVariant.Secondary}
                />
              </Can>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailInfoRow label={t("fab:VIN")} value={renderText(vehicle.vin)} mono />
          <DetailInfoRow label={t("fab:LicensePlate")} value={renderText(vehicle.licensePlate)} />
          <DetailInfoRow label={t("fab:Brand")} value={renderText(vehicle.model.brandName)} />
          <DetailInfoRow label={t("masterdata:ModelDescription")} value={renderText(vehicle.model.modelDescription)} />
          <DetailInfoRow
            label={t("fab:EngineKilowatts")}
            value={automaticCellRenderer(normalizeText(vehicle.engineKilowatts), {
              numberDecimals: 0,
              globalSuffix: " kW",
            })}
          />
          <DetailInfoRow
            label={t("fab:Color")}
            value={renderText(getVehicleBuyInColorLabel(vehicle.color, i18n.language))}
          />
          <DetailInfoRow label={t("fab:ManufacturerColor")} value={renderText(vehicle.manufacturerColor)} />
          <DetailInfoRow
            label={t("masterdata:VatDeductible")}
            value={getVehicleBooleanLabel(vehicle.vatDeductible, t)}
          />
          <DetailInfoRow label={t("masterdata:Reimport")} value={getVehicleBooleanLabel(vehicle.reimport, t)} />
          <DetailInfoRow label={t("fab:NumberOfDoors")} value={renderInteger(vehicle.numberOfDoors)} />
          <DetailInfoRow label={t("common:RegistrationDate")} value={renderDate(vehicle.registrationDate)} />
          <DetailInfoRow label={t("fab:PreviousHolders")} value={renderInteger(vehicle.numberOfHolders)} />
          <DetailInfoRow
            label={t("fab:VehicleUsage")}
            value={
              isVehicleUsage(vehicle.usage) ? t(VEHICLE_USAGE_LABEL_KEYS[vehicle.usage]) : renderText(vehicle.usage)
            }
          />
          <DetailInfoRow label={t("fab:CurrentMileage")} value={renderInteger(vehicle.currentMileage, " km")} />
          <DetailInfoRow label={t("fab:HuDate")} value={renderDate(vehicle.huDate)} />
          <DetailInfoRow
            label={t("fab:ServiceHistory")}
            value={
              isServiceHistoryStatus(vehicle.serviceHistory)
                ? t(SERVICE_HISTORY_LABEL_KEYS[vehicle.serviceHistory])
                : renderText(vehicle.serviceHistory)
            }
          />
        </CardContent>
      </Card>

      <div className={vehicle.exchangeEngine ? "grid gap-6 xl:grid-cols-2" : undefined}>
        <Card>
          <CardHeader>
            <CardTitle>{t("fab:Accident")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <DetailInfoRow
              label={t("fab:AccidentState")}
              value={getVehicleAccidentStateLabel(vehicle.accident.state, t)}
            />
            <DetailInfoRow
              label={t("fab:AccidentValue")}
              value={
                shouldShowVehicleAccidentDetails(vehicle.accident.state)
                  ? renderCurrency(vehicle.accident.value)
                  : undefined
              }
            />
            <DetailInfoRow
              label={t("fab:AccidentDescription")}
              value={renderText(vehicle.accident.description)}
              fullWidth
            />
          </CardContent>
        </Card>

        {vehicle.exchangeEngine ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("fab:ExchangeEngine")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <DetailInfoRow label={t("fab:ExchangeDate")} value={renderDate(vehicle.exchangeEngine.exchangeDate)} />
              <DetailInfoRow
                label={t("fab:VehicleMileageAtExchange")}
                value={renderInteger(vehicle.exchangeEngine.vehicleMileageAtExchange, " km")}
              />
              <DetailInfoRow
                label={t("fab:EngineMileageAtExchange")}
                value={renderInteger(vehicle.exchangeEngine.engineMileageAtExchange, " km")}
              />
              <DetailInfoRow
                label={t("fab:ExchangeEngineNotes")}
                value={renderText(vehicle.exchangeEngine.notes)}
                fullWidth
              />
            </CardContent>
          </Card>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("fab:BuyInDetails")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <DetailInfoRow
            label={t("fab:VehicleBuyInType")}
            value={
              vehicleBuyIn.type ? t(VEHICLE_BUY_IN_TYPE_LABEL_KEYS[vehicleBuyIn.type]) : renderText(vehicleBuyIn.type)
            }
          />
          <DetailInfoRow label={t("fab:TradeInReference")} value={renderText(vehicleBuyIn.tradeInReference)} />
          <DetailInfoRow
            label={t("fab:TransferDate")}
            value={renderText(apiYearMonthToInputString(purchase.transferDate))}
          />
          <DetailInfoRow label={t("fab:MileageAtBuyIn")} value={renderInteger(vehicle.mileageAtBuyIn, " km")} />
          <DetailInfoRow label={t("fab:PurchasePrice")} value={renderCurrency(purchase.price)} />
          <DetailInfoRow
            label={t("fab:VatAmount")}
            value={vehicle.vatDeductible ? renderCurrency(purchase.vatAmount) : undefined}
          />
          <DetailInfoRow label={t("fab:OtherAgreements")} value={renderText(purchase.otherAgreements)} fullWidth />
        </CardContent>
      </Card>
    </div>
  );
}
